import { refuseUnported } from '../janus-engine/unported';

// campaign-time-init.ts — CAMPAIGN TIME / FAST-FORWARD, carved out of template.js and TS-ported for Phase 2a
// of plans/bundler-script-load-order-phase2.md — a member of the post-template-mid aggregate.
//
// MODULE-SCOPE SAFE: the original states "Side effects on load: NONE" — at eval only the FT_PUSH_CHUNK_ROUNDS
// const + function defs + 10 window attaches. Deps (queryData / getSidelogContext / validateContext /
// initializeFateTideCaches / syncMyCampaignPlayPauseStopCluster / displayWarning (template.js), BASE_URL
// (core-helpers)) are all click-time. State lives under window.FateTide.* (already global).
//
// PUBLIC SURFACE (10 identifiers, all window-attached, all PHP-onclick / page-load callers in my_campaign.php
// — no module-private internals). FT_PUSH_CHUNK_ROUNDS is a const so its window assignment is REQUIRED.

import { reportTimePushRefusal } from '../janus-engine/telemetry/time-push-refusal';

const FT_PUSH_CHUNK_ROUNDS = 10;

function campaignFastForwardStorageKey(campaignId: string): string {
    return `ft.campaign.${campaignId}.fastForward`;
}

function getCampaignFastForward(campaignId: string): boolean {
    if (!campaignId) {
        return false;
    }
    try {
        const v = localStorage.getItem(campaignFastForwardStorageKey(campaignId));
        if (v === '1') {
            return true;
        }
        if (v === '0') {
            return false;
        }
    } catch (_) {
        /** storage blocked */
    }
    return !!(window.FateTide?.campaignFastForward?.[campaignId]);
}

function setCampaignFastForward(campaignId: string, on: boolean): void {
    if (!campaignId) {
        return;
    }
    initializeFateTideCaches();
    window.FateTide.campaignFastForward[campaignId] = !!on;
    try {
        localStorage.setItem(
            campaignFastForwardStorageKey(campaignId),
            on ? '1' : '0',
        );
    } catch (_) {
        /** quota / privacy mode */
    }
}

function applyCampaignFastForwardButtonVisual(btnEl: HTMLElement | null | undefined, on: boolean): void {
    if (!btnEl) {
        return;
    }
    btnEl.setAttribute('aria-pressed', on ? 'true' : 'false');
    btnEl.classList.remove(
        'btn-success',
    );
    if (on) {
        btnEl.classList.add('btn-light');
        btnEl.classList.remove(
            'btn-dark',
            'border',
        );
    } else {
        btnEl.classList.remove('btn-light');
        btnEl.classList.add(
            'btn-dark',
            'border',
        );
    }
}

function toggleCampaignFastForward(campaignId: string, btnEl: HTMLElement | null | undefined): void {
    const next = !getCampaignFastForward(campaignId);
    setCampaignFastForward(campaignId, next);
    applyCampaignFastForwardButtonVisual(btnEl, next);
    syncMyCampaignPlayPauseStopCluster();
}

function requestStopCampaignTimeSkip(): void {
    initializeFateTideCaches();
    window.FateTide.cancelCampaignTimeSkipRequested = true;
}

function setCampaignTimeSkipStopVisible(show: boolean): void {
    initializeFateTideCaches();
    window.FateTide.campaignTimeChunkStopUiActive = !!show;
    syncMyCampaignPlayPauseStopCluster();
}

async function pushCampaignTime(
    rounds: number | string,
    gm_insert_log_message: boolean = true,
    opts: { skipEncounterPushEachChunk?: boolean; forceGranularChunks?: boolean } | null = null,
): Promise<boolean | undefined> {
    if (window.ftBaseCommitsPaused?.()) { refuseUnported('engine-version-reload'); return true; }
    const forceGranularChunks = !!(opts && opts.forceGranularChunks);

    const sl_context = getSidelogContext();
    if (!sl_context) return;

    const {
        roll_private,
    } = sl_context;
    const campaign_id = String(sl_context.campaign_id || '');
    const encounter_id = String(sl_context.encounter_id || '');
    const fastForward = !forceGranularChunks && getCampaignFastForward(campaign_id);

    const rules = {
        encounter_id: { type: 'uuid', value: encounter_id },
        campaign_id: { type: 'uuid', value: campaign_id },
    };
    const validationResult = validateContext(rules, 'pushCampaignTime');
    if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }

    const total = Math.max(0, Number.parseInt(String(rounds), 10));
    if (
        total === 0
        || !Number.isFinite(total)
    ) {
        return;
    }

    initializeFateTideCaches();
    if (window.FateTide.pushTimeInFlight[campaign_id]) return;
    window.FateTide.pushTimeInFlight[campaign_id] = true;

    let cancelled = false;
    let stopUiShown = false;
    // Every chunk uses the browser decision and the same lazy-save endpoint. A missing
    // resolver or a refused/unknown save stops this request without a second clock write.
    const tryTruth = async (roundsToPush: number, final: boolean, committedRounds: number): Promise<boolean> => {
        const failure = { campaignId: campaign_id, encounterId: encounter_id };
        let refused: string | undefined;
        const entry = (window as unknown as { ftTryCommitTimePush?: (input: { campaignId: string; encounterId: string; rounds: number; fastForward?: boolean; rollPrivate?: number; gmLog?: boolean; logSeconds?: number; onRefused?: (reason: string) => void; onCommittedClock?: (clock: Record<string, number>) => void }) => Promise<'handled' | 'unresolved' | null> }).ftTryCommitTimePush;
        if (typeof entry !== 'function') {
            reportTimePushRefusal(failure, 'resolver-not-loaded');
            return false;
        }
        try {
            const result = await entry({ campaignId: campaign_id, encounterId: encounter_id, rounds: roundsToPush, fastForward, rollPrivate: Number(roll_private ?? 2),
                gmLog: final && gm_insert_log_message, logSeconds: (committedRounds + roundsToPush) * 6,
                onRefused: reason => { refused = reason; },
                onCommittedClock: clock => {
                    const campaign = (window.FateTide as unknown as { campaignCache?: Map<string, { campaigns?: Record<string, unknown> }> })?.campaignCache?.get(campaign_id);
                    if (campaign) for (const [key, value] of Object.entries(clock)) (campaign.campaigns ??= {})['campaign_time_' + key] = value;
                } });
            if (result !== 'handled') {
                if (result !== 'unresolved' && !refused) reportTimePushRefusal(failure, 'entry-returned-without-reason');
                return false;
            }
            return true;
        } catch (error) {
            console.warn('time push: browser entry threw; clock outcome may be unknown', error);
            if (!refused) reportTimePushRefusal(failure, 'entry-threw', { error: String(error instanceof Error ? error.message : error) }, true);
            return false;
        }
    };
    try {
        const retry = (window as unknown as { ftRetryUnresolvedTimePush?: (id: string) => Promise<'handled' | 'unresolved' | null>; ftHasUnresolvedTimePush?: (id: string) => boolean });
        if (retry.ftHasUnresolvedTimePush?.(campaign_id)) {
            await retry.ftRetryUnresolvedTimePush?.(campaign_id);
            return true; // this click recovered the prior push; it never starts the requested span again
        }
        const chunkSize = forceGranularChunks ? FT_PUSH_CHUNK_ROUNDS : 14400;
        if (total > chunkSize) {
            stopUiShown = true;
            window.FateTide.cancelCampaignTimeSkipRequested = false;
            setCampaignTimeSkipStopVisible(true);
        }
        let remaining = total;
        while (remaining > 0) {
            if (stopUiShown && window.FateTide.cancelCampaignTimeSkipRequested) {
                cancelled = true;
                window.FateTide.cancelCampaignTimeSkipRequested = false;
                break;
            }
            const thisChunk = Math.min(chunkSize, remaining);
            if (!await tryTruth(thisChunk, thisChunk === remaining, total - remaining)) return true;
            remaining -= thisChunk;
        }
    } finally {
        if (stopUiShown) setCampaignTimeSkipStopVisible(false);
        window.FateTide.pushTimeInFlight[campaign_id] = false;
    }

    if (cancelled) {
        displayWarning('Campaign time advance stopped before finishing the full span.');
        return true;
    }
    return false;
}

async function pullCampaignTime(rounds: number | string, gm_insert_log_message: boolean): Promise<void> {
    const campaignId = window.ftGetCampaignId?.() || getSidelogContext()?.campaign_id || '';
    const seconds = -Math.abs(Math.trunc(Number(rounds))) * 6;
    if (!campaignId || !Number.isFinite(seconds) || seconds === 0) return;
    const entry = (window as unknown as { ftTryCommitCalendarClock?: (input: { campaignId: string; seconds: number; gmLog: boolean }) => Promise<boolean> }).ftTryCommitCalendarClock;
    if (!entry) { refuseUnported('calendar-light:pull-plan-unavailable'); return; }
    await entry({ campaignId, seconds, gmLog: gm_insert_log_message });
}


// ===========================================================================================
// Cross-file consumer exports — all 10 public identifiers are called from outside this file (PHP onclick
// in my_campaign.php + inline page-load script). FT_PUSH_CHUNK_ROUNDS is a const so the window assignment
// is REQUIRED — consts don't auto-attach to window.
// ===========================================================================================
declare global {
    interface CampaignTimeState {
        campaignFastForward: Record<string, boolean>;
        pushTimeInFlight: Record<string, boolean>;
        cancelCampaignTimeSkipRequested: boolean;
        campaignTimeChunkStopUiActive: boolean;
    }

    interface Window {
        FateTide: CampaignTimeState;
        ftGetCampaignId?: () => string;
        FT_PUSH_CHUNK_ROUNDS: typeof FT_PUSH_CHUNK_ROUNDS;
        campaignFastForwardStorageKey: typeof campaignFastForwardStorageKey;
        getCampaignFastForward: typeof getCampaignFastForward;
        setCampaignFastForward: typeof setCampaignFastForward;
        applyCampaignFastForwardButtonVisual: typeof applyCampaignFastForwardButtonVisual;
        toggleCampaignFastForward: typeof toggleCampaignFastForward;
        requestStopCampaignTimeSkip: typeof requestStopCampaignTimeSkip;
        setCampaignTimeSkipStopVisible: typeof setCampaignTimeSkipStopVisible;
        pushCampaignTime: typeof pushCampaignTime;
        pullCampaignTime: typeof pullCampaignTime;
    }
}

window.FT_PUSH_CHUNK_ROUNDS = FT_PUSH_CHUNK_ROUNDS;
window.campaignFastForwardStorageKey = campaignFastForwardStorageKey;
window.getCampaignFastForward = getCampaignFastForward;
window.setCampaignFastForward = setCampaignFastForward;
window.applyCampaignFastForwardButtonVisual = applyCampaignFastForwardButtonVisual;
window.toggleCampaignFastForward = toggleCampaignFastForward;
window.requestStopCampaignTimeSkip = requestStopCampaignTimeSkip;
window.setCampaignTimeSkipStopVisible = setCampaignTimeSkipStopVisible;
window.pushCampaignTime = pushCampaignTime;
window.pullCampaignTime = pullCampaignTime;

export {};
